///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2026, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2026 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { IHttpClient } from "./IHttpClient";
import { Endpoint } from "./Endpoint";
import { IRoleActions } from "./IRole";

/**
 * A role determines what actions allowed to be performed by {@link User | users} on a
 * {@link Project | project}.
 */
export class Role extends Endpoint {
  private _data: any;
  public projectId: string;

  /**
   * @param data - Raw role data received from the server. For more information, see
   *   {@link https://cloud.opendesign.com/docs//pages/server/api.html#Project | Open Cloud Projects API}.
   * @param projectId - Owner project ID.
   * @param httpClient - HTTP client instance used to send requests to the REST API server.
   */
  constructor(data: any, projectId: string, httpClient: IHttpClient) {
    super("", httpClient);
    this.projectId = projectId;
    this.data = data;
  }

  /**
   * Role description.
   */
  get description(): string {
    return this.data.description;
  }

  set description(value: string) {
    this._data.description = value;
  }

  /**
   * Raw role data received from the server. For more information, see
   * {@link https://cloud.opendesign.com/docs//pages/server/api.html#Project | Open Cloud Projects API}.
   *
   * @readonly
   */
  get data(): any {
    return this._data;
  }

  set data(value: any) {
    this._data = value;
    this.path = `/projects/${this.projectId}/roles/${value.name}`;
  }

  /**
   * Role name.
   */
  get name(): string {
    return this.data.name;
  }

  set name(value: string) {
    this._data.name = value;
  }

  /**
   * Role actions are allowed to be performed.
   */
  get permissions(): IRoleActions {
    return this.data.permissions;
  }

  set permissions(value: IRoleActions) {
    this.data.permissions = value || {};
  }

  /**
   * Reloads role data from the server.
   */
  async checkout(): Promise<this> {
    const response = await this.get("");
    this.data = await response.json();
    return this;
  }

  /**
   * Updates role data on the server.
   *
   * @param data - Raw role data. For more information, see
   *   {@link https://cloud.opendesign.com/docs//pages/server/api.html#Project | Open Cloud Projects API}.
   */
  async update(data: any): Promise<this> {
    const response = await this.put("", data);
    this.data = await response.json();
    return this;
  }

  /**
   * Deletes a role from the project.
   *
   * @returns Returns the raw data of a deleted role. For more information, see
   *   {@link https://cloud.opendesign.com/docs//pages/server/api.html#Project | Open Cloud Projects API}.
   */
  override delete(): Promise<any> {
    return super.delete("").then((response) => response.json());
  }

  /**
   * Saves role properties changes to the server. Call this method to update role data on the server
   * after any property changes.
   */
  save(): Promise<this> {
    return this.update(this.data);
  }
}
